import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import { useAuth } from '@/context/auth-context';

const CONFIGURADOR_URL =
  import.meta.env.VITE_CONFIGURADOR_URL || 'https://bconfig-web.staging.bws-hub.com';
const CONFIGURADOR_ORIGIN = new URL(CONFIGURADOR_URL).origin;

// Rotas do MFE que o menu do bcube expõe como subitens. O sufixo do path do bcube
// (/configurador-sso/<rota>) vira o path do iframe (<CONFIGURADOR_URL>/<rota>).
// Whitelist em vez de repasse direto: o que não estiver aqui abre a home do MFE.
const MFE_ROUTES = [
  'configurator',
  'config-profiles',
  // Alvos dos botões "Criar Perfil"/"ver perfil" no step de configurações da
  // proposta. `config-profiles/edit` recebe o id por `?id=`, como a rota antiga.
  'config-profiles/create',
  'config-profiles/edit',
  'auto-test',
  'identificator',
  'check-configuration',
  'commands',
] as const;

// `search` entra separado porque o splat do react-router não inclui a querystring,
// e a tela de edição do MFE lê o perfil de `?id=`.
function resolveMfeUrl(subRoute: string | undefined, search = ''): string {
  const route = (subRoute ?? '').replace(/^\/+|\/+$/g, '');
  if (!MFE_ROUTES.includes(route as (typeof MFE_ROUTES)[number])) return CONFIGURADOR_URL;
  return `${CONFIGURADOR_URL}/${route}${search}`;
}

export function ConfiguradorSsoPage() {
  // O MFE roda em browser router quando embutido, então a tela inicial vem pela URL
  // do iframe. Trocar de subitem troca o `src` e recarrega o MFE — o host-bridge só
  // fala READY/TOKEN/LOGOUT, não há como navegar por postMessage sem reload.
  const params = useParams();
  const { search } = useLocation();
  const src = resolveMfeUrl(params['*'], search);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Whether the MFE has announced itself with BWS_CONFIGURADOR_READY. Only after
  // that is its message listener attached and its origin known to be safe to push to.
  const isReadyRef = useRef(false);
  // Last token we sent to the iframe, so we can detect renewals and re-send.
  const lastSentTokenRef = useRef<string | null>(null);

  const { isAuthenticated } = useAuth();

  const sendToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'BWS_AUTH_TOKEN', token },
      CONFIGURADOR_ORIGIN,
    );
    lastSentTokenRef.current = token;
  }, []);

  // Handshake: reply to BWS_CONFIGURADOR_READY with the current bcube token.
  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.origin !== CONFIGURADOR_ORIGIN) return;
      if (event.data?.type !== 'BWS_CONFIGURADOR_READY') return;

      isReadyRef.current = true;
      sendToken();
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [sendToken]);

  // Re-send the token whenever it is renewed at runtime. The MFE accepts updates
  // and refreshes its own Configurador session from the new token. We watch both
  // cross-tab writes (storage event) and same-tab writes (short interval), since
  // localStorage does not emit `storage` in the tab that performed the write.
  useEffect(() => {
    function pushIfChanged() {
      if (!isReadyRef.current) return;
      const token = localStorage.getItem('token');
      if (token && token !== lastSentTokenRef.current) {
        sendToken();
      }
    }

    window.addEventListener('storage', pushIfChanged);
    const interval = window.setInterval(pushIfChanged, 2000);
    return () => {
      window.removeEventListener('storage', pushIfChanged);
      window.clearInterval(interval);
    };
  }, [sendToken]);

  // On bcube logout, tell the MFE so it can drop its session (expired screen).
  useEffect(() => {
    if (isAuthenticated) return;
    if (!isReadyRef.current) return;
    iframeRef.current?.contentWindow?.postMessage({ type: 'BWS_AUTH_LOGOUT' }, CONFIGURADOR_ORIGIN);
    lastSentTokenRef.current = null;
  }, [isAuthenticated]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      // Trocar de subitem recarrega o iframe: a instância antiga morre e a nova
      // reenvia READY. Zeramos o handshake aqui para não tratar como "pronto" um
      // contentWindow que já não existe, nem pular o reenvio do token.
      onLoad={() => {
        isReadyRef.current = false;
        lastSentTokenRef.current = null;
      }}
      title="Configurador"
      allow="serial; usb"
      className="h-[calc(100vh-8rem)] w-full border-0"
    />
  );
}
